import React from "react"
import { FormikProvider, useFormik } from "formik"
import * as yup from "yup"
import { observer } from "mobx-react-lite"
import clsx from "clsx"
import filter from "lodash/filter"
import identity from "lodash/identity"
import uniqBy from "lodash/uniqBy"
import { nanoid } from "nanoid"

import Text from "@components/ui/Typography/Text"
import { useController, useStore } from "@store"
import Templates from "@components/ui/Templates"
import ProductsField from "@components/prototypes/ProductPicker/MultipleProductsField"
import { Option } from "@framework/types/utils"

import Footer from "./ReportTemplateFooter"

import styles from "./WorkbookReportTemplate.module.sass"

export interface ProductComparisonFormProps {
  initialData?: any
  loading?: boolean
  onSubmit?: () => Promise<void | boolean> | void | boolean
  onClose?: () => void
}

const minProductsNum = 2

const validationSchema = yup.object({
  products: yup
    .array()
    .label("Products")
    .of(yup.string().required().default(""))
    .min(
      minProductsNum,
      `at least ${minProductsNum} products needed for comparison`
    )
    .required()
    .default([]),
})

type FormData = yup.InferType<typeof validationSchema>

export const renderQuery = (products: string[]) => {
  const productsText = products.length
    ? `${products.map((it) => `[${it}]`).join(" vs ")}`
    : "[Not provided]"

  return `Generate a comparison report for products ${productsText}`
}

const ProductComparisonForm: React.FC<ProductComparisonFormProps> = observer(
  ({ initialData, loading, onSubmit, onClose }) => {
    const { productComparisonStore } = useStore()

    const { unifiedMatrixController, factFinderSolutionController } =
      useController()

    const [isLoading, setLoading] = React.useState(false)

    const initialProductOptions: Option[] = React.useMemo(() => {
      const { products = [] } = initialData
      const options = filter(
        [
          ...products.map((it: any) => {
            return {
              name: it.id ?? `local_${nanoid()}`,
              value: it.name,
            }
          }),
        ],
        identity
      )
      return uniqBy(options, (it) => it.name)
    }, [])

    const initialValues = React.useMemo(() => {
      return validationSchema.cast(
        {
          ...initialData,
          products: initialProductOptions.map((it) => it.name),
        },
        {
          stripUnknown: true,
        }
      )
    }, [])

    const formik = useFormik<FormData>({
      initialValues: validationSchema.cast(initialValues, {
        stripUnknown: true,
      }),
      validationSchema,
      validateOnChange: false,
      validateOnBlur: true,
      onSubmit: async (data) => {
        try {
          setLoading(true)

          const mapProductIdToName = (id: string) => {
            return (
              productComparisonStore.productStore.getProductById(id)?.name ??
              initialProductOptions.find((it) => it.name === id)?.value ??
              null
            )
          }

          const products = data.products.map((id) => {
            const item = mapProductIdToName(id)
            if (item == null) throw new Error("Unable to load product")
            return item
          })

          const query = renderQuery(products)

          const store = factFinderSolutionController.pushReport({
            type: "PRODUCTCOMPARISON",
            query,
            data,
          })

          if (store == null) return

          const shouldClose = (await onSubmit?.()) ?? true

          const res =
            await unifiedMatrixController.generateProductComparisonReport(
              products
            )

          if (res.status === "SUCCESS") {
            store.setData(res.data)

            if (shouldClose) onClose?.()
            return
          }

          const error =
            "Unexpected error while report generation. Please try again later"

          store.setError(error)
        } finally {
          setLoading(false)
        }
      },
    })

    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className={clsx(styles.form)}>
          <Templates.RollScript
            gutter="24"
            footerSocket={
              <Footer isLoading={isLoading || loading} onClose={onClose} />
            }
          >
            <div className={styles.inlineFlow}>
              <Text variant="body1" className={styles.messageText}>
                Generate a comparison report for products
              </Text>

              <div className={styles.input}>
                <ProductsField
                  name="products"
                  placeholder={`Select ${minProductsNum} and more products...`}
                  clearable={false}
                  disabled={isLoading}
                  defaultOptions={initialProductOptions}
                />
              </div>
            </div>
          </Templates.RollScript>
        </form>
      </FormikProvider>
    )
  }
)

export default ProductComparisonForm
